'use client';

import * as Accordion from '@radix-ui/react-accordion';
import { Plus } from 'lucide-react';
import DOMPurify from 'isomorphic-dompurify';
import { mergeClassNames } from '@/lib/utils';
import { DESCRIPTION_PROSE } from '@/lib/prose';

export interface FaqItem {
  question: string;
  answer: string;
  order: number;
}

interface FaqAccordionItemProps {
  faq: FaqItem;
  value: string;
  className?: string;
  isLast?: boolean;
  compact?: boolean;
}

const PlusMinusIcon = ({ className }: { className?: string }) => (
  <Plus
    size={24}
    strokeWidth={2}
    className={mergeClassNames(
      'shrink-0',
      '[&>path:last-child]:origin-center [&>path:last-child]:[transform-box:fill-box]',
      '[&>path:last-child]:transition-[transform,opacity] [&>path:last-child]:duration-[350ms] [&>path:last-child]:ease-[cubic-bezier(0.4,0,0.2,1)]',
      'group-data-[state=open]:[&>path:last-child]:rotate-90 group-data-[state=open]:[&>path:last-child]:opacity-0',
      className
    )}
  />
);

export const FaqAccordionItem = ({
  faq,
  value,
  className,
  isLast = false,
  compact = false,
}: FaqAccordionItemProps) => {
  return (
    <Accordion.Item
      value={value}
      className={mergeClassNames('group', className)}
    >
      <Accordion.Header>
        <Accordion.Trigger
          className={mergeClassNames(
            'group/trigger flex w-full items-center justify-between gap-4 text-left cursor-pointer',
            isLast
              ? 'pt-5 group-data-[state=open]:pt-5 group-data-[state=open]:pb-4'
              : 'py-5 group-data-[state=open]:pb-4',
            compact && 'group-data-[state=open]:pb-2'
          )}
        >
          <span
            className={mergeClassNames(
              'text-base font-semibold text-gray-900 transition-colors duration-200 ease-out group-hover/trigger:text-red-400 sm:text-lg',
              compact && 'text-sm sm:text-base'
            )}
          >
            {faq.question}
          </span>
          <PlusMinusIcon className="text-red-400" />
        </Accordion.Trigger>
      </Accordion.Header>

      <Accordion.Content
        forceMount
        className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] data-[state=open]:grid-rows-[1fr]"
      >
        <div className="overflow-hidden">
          <div
            className={mergeClassNames(
              DESCRIPTION_PROSE,
              'sm:text-base',
              'opacity-0 -translate-y-2 transition-[opacity,transform] duration-300 ease-out',
              'group-data-[state=open]:opacity-100 group-data-[state=open]:translate-y-0 group-data-[state=open]:delay-[80ms]',
              !isLast && 'pb-4'
            )}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(faq.answer ?? ''),
            }}
          />
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};
